/**
 * What the studio shows when nobody is signed in.
 *
 * Landing here used to print the API's own words — "Missing Bearer token" —
 * above an empty page. That is accurate and useless: it reads as a fault in the
 * app rather than as the normal state of a tool you have not signed into yet,
 * and it says nothing about what to do next.
 *
 * This app deliberately has no password of its own. Identity comes from the
 * platform, so the honest thing to say is "the platform lets you in", and then
 * say how.
 */

import * as entra from '../auth/entra';

interface Props {
  /** Where the CoFounder lives, when this build knows. */
  platformUrl?: string;
  /**
   * `studio` is someone who navigated here to build something. `embed` is a
   * report — reached by a shared link, or drawn inside the platform, where a
   * 401 more often means the handoff failed than that the reader took a wrong
   * turn. Same cause, two different next actions, so they read differently.
   */
  context?: 'studio' | 'embed';
}

export const SignedOut = ({ platformUrl = 'https://platform.applockr.com', context = 'studio' }: Props) =>
  context === 'studio' && entra.enabled() ? (
    <div className="signed-out">
      <h2>Sign in to build reports</h2>
      <p>
        The report builder uses your appLockr account, so the apps and partner data you can see here
        are exactly the ones you can see on the platform.
      </p>
      <p>
        <button type="button" className="primary" onClick={() => void entra.signIn()}>
          Sign in with appLockr
        </button>
      </p>
      <p className="signed-out__note">
        Building and deleting charts needs the <b>Reports Author</b> role. If sign-in succeeds and
        you still cannot get in, that assignment is what is missing — the error will say so.
      </p>
      <p className="signed-out__note">
        Nothing here needs an account to look at: <a href="/preview">/preview</a> renders the whole
        chart library against sample data.
      </p>
    </div>
  ) : context === 'embed' ? (
    <div className="signed-out">
      <h2>This report needs a session</h2>
      <p>
        Reports take their identity from appLockr rather than having a login of their own, and this
        one arrived without a valid session.
      </p>
      <ul>
        <li>
          <b>Opened this link directly?</b> Show a report in the{' '}
          <b>AI CoFounder</b> on <a href={platformUrl}>{new URL(platformUrl).host}</a> first, then
          reload this tab. That signs you in for 12 hours.
        </li>
        <li>
          <b>Seeing this inside appLockr?</b> The sign-in handoff did not complete. Reloading the
          page usually settles it; if it does not, the report is worth reporting rather than
          retrying.
        </li>
      </ul>
      <p className="signed-out__note">
        Sessions are held in memory and never written to browser storage, so a refresh of the
        report itself ends one.
      </p>
    </div>
  ) : (
  <div className="signed-out">
    <h2>Sign in through appLockr</h2>
    <p>
      The report builder has no login of its own — it takes your identity from the platform, so the
      apps and partner data you can see here are exactly the ones you can see there.
    </p>

    <ol>
      <li>
        Open the <b>AI CoFounder</b> on <a href={platformUrl}>{new URL(platformUrl).host}</a> and
        show a report. That signs this app in for <b>12 hours</b>.
      </li>
      <li>Come back to this tab and reload.</li>
    </ol>

    <p className="signed-out__note">
      <b>A refresh signs you out.</b> The session is held in memory and never written to browser
      storage, so it cannot be replayed from another tab — the trade is that reloading this page
      ends it. Finish an edit before you reload.
    </p>

    <p className="signed-out__note">
      Nothing here needs a session to look at: <a href="/preview">/preview</a> renders the whole
      chart library against sample data.
    </p>
  </div>
);
